/**
 * System notifications for the on-device model bring-up.
 *
 * The model is ~1.1 GB and takes minutes on a normal connection, which is long
 * enough that people switch tabs. These are the three moments worth pulling
 * them back for: it finished, it failed, or it has gone quiet.
 *
 * Two rules, both deliberate:
 *
 * - **Never prompts.** `Notification.requestPermission()` is only ever called
 *   from an explicit user action (see `useNotificationOptIn`); a background
 *   download is not the moment to throw a permission dialog. If permission
 *   hasn't been granted these are silent no-ops.
 * - **Only when the tab is hidden.** Someone watching the loading screen does
 *   not need a banner restating what the screen already says.
 *
 * Mirrors `ModelDownloadAlerts` in the Flutter app. The mobile client also
 * shows ongoing progress; the web has no equivalent of an in-place-updating
 * notification, so only the terminal states are surfaced here.
 */

const READY_TAG = 'kivo-model-ready';
const FAILED_TAG = 'kivo-model-failed';
const STALLED_TAG = 'kivo-model-stalled';

/** The route a notification click should land on. */
export const MODEL_DEEP_LINK = '/ai-chat';

function canNotify(): boolean {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission !== 'granted') return false;
  // `undefined` in a non-DOM test environment — treat as visible, i.e. stay
  // quiet, which is the safe default.
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function show(title: string, body: string, tag: string): void {
  if (!canNotify()) return;
  try {
    // `tag` makes a repeat replace its predecessor rather than stacking.
    const n = new Notification(title, { body, tag, icon: '/logo192.png' });
    n.onclick = () => {
      try {
        window.focus();
        if (window.location.pathname !== MODEL_DEEP_LINK) {
          window.location.assign(MODEL_DEEP_LINK);
        }
      } catch {
        /* a blocked focus() must not take the handler down with it */
      }
      n.close();
    };
  } catch {
    /* Notification construction throws on some mobile browsers — never fatal */
  }
}

export function notifyModelReady(): void {
  show('Your AI assistant is ready', 'Tap to start chatting.', READY_TAG);
}

export function notifyModelFailed(message: string): void {
  show('Model loading failed', message, FAILED_TAG);
}

export function notifyModelStalled(): void {
  show(
    'Download stalled',
    'No data is coming through. Check your connection — finished files are kept.',
    STALLED_TAG,
  );
}
